/** P32 (Feb 27, 2026) — Smart date input with MM/DD/YYYY Day display. */
import React, { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';

import { dayOfWeek, formatDateMDY, smartParseDate } from '../utils/dates';

/** Smart date input — displays "MM/DD/YYYY Day" while not focused and
 *  lets the user freely type any of the smartParseDate-supported formats
 *  while focused. Commits on blur + Enter.
 */
function SmartDateInput({ value, onChange, onCommit, testId }) {
  const [draft, setDraft] = useState(value ? `${formatDateMDY(value)} ${dayOfWeek(value)}`.trim() : '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(value ? `${formatDateMDY(value)} ${dayOfWeek(value)}`.trim() : '');
    }
  }, [value, focused]);

  const commit = (raw) => {
    // Strip trailing day-of-week if present (e.g. "05/01/2026 Fri" → "05/01/2026")
    const stripped = String(raw || '').replace(/\s+[A-Za-z]{3,}\s*$/, '').trim();
    const parsed = smartParseDate(stripped);
    if (parsed) {
      onChange(parsed);
      setDraft(`${formatDateMDY(parsed)} ${dayOfWeek(parsed)}`.trim());
      if (onCommit) onCommit(parsed);
    } else if (stripped === '') {
      onChange('');
      setDraft('');
    }
  };

  return (
    <div className="relative" data-testid={testId}>
      <Input
        type="text"
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => { setFocused(false); commit(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(e.target.value); }
        }}
        placeholder="MM/DD/YYYY or 15"
        data-testid={`${testId}-input`}
      />
      {/* Hidden ISO sentinel so testing tools can still assert against the canonical date */}
      <input type="hidden" data-testid={`${testId}-iso`} value={value || ''} readOnly />
      <span className="sr-only" data-testid={`${testId}-dow`}>{dayOfWeek(value)}</span>
    </div>
  );
}

export default SmartDateInput;
